import { ComponentProps, PropsWithChildren, ReactElement, useMemo, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { z } from 'zod';
import { useGetParkingSharingAvailability, useGetParkingSharingRequest } from '_api/useParkingSharing';
import { useOwnProfile } from '_api/useProfile';
import { SafeAreaView } from '_dependencies/safeArea';
import { useAppNavigation } from '_navigator';
import { useChatWithNeighbour, useUpdatingFormattedTimeAgo, WW } from '_utils';
import { track } from '_utils/Amplitude';
import formatBookingTimeRange from '_utils/formatBookingTimeRange';
import safeParse from '_utils/safeParse';
import { screenMargin, smallestMargin } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import {
    ContextMenu,
    HeaderWithNav,
    HeimeText,
    Icon,
    PrimaryButton,
    QueryItemView,
    SecondaryButton,
    UserListItem,
} from 'Components';
import { RoutePropsSchema } from 'types/Utility';
import BookingSharingSelectedSkeletonLoader from './components/BookingSharingSelectedSkeletonLoader';
import DeleteBookingSharingModal from './components/DeleteBookingSharingModal';
import BookingSharingDetails from '../common/BookingSharingDetails';
import BookingSharingItem from '../common/BookingSharingItem';
import BookingSharingTag from '../common/BookingSharingTag';
import getAvailabilityNonTakenSlots from '../common/getAvailabilityNonTakenSlots';
import useGetAssignedParkingAvailability from '../common/useGetAssignedParkingAvailability';

const propsSChema = RoutePropsSchema(
    z.union([
        z.object({
            type: z.literal('request'),
            requestId: z.string(),
        }),
        z.object({
            type: z.literal('availability'),
            availabilityId: z.string(),
        }),
    ]),
);

const BookingSharingRequestSelected = (props: z.infer<typeof propsSChema>): ReactElement => {
    const params = safeParse(props, propsSChema).route.params;
    const { navigate, canGoBack, goBack } = useAppNavigation();
    const { t } = useTranslation();
    const { theme } = useThemeContext();
    const { onChat, isLoading: isLoadingChat } = useChatWithNeighbour();
    const { data, error, status, refetch, isRefetching } =
        params.type === 'request'
            ? // We know what we are doing! Parent guarantees type will not change!
              // eslint-disable-next-line react-hooks/rules-of-hooks
              useGetParkingSharingRequest(params.requestId.toString())
            : // eslint-disable-next-line react-hooks/rules-of-hooks
              useGetParkingSharingAvailability(params.availabilityId.toString());
    const ownId = useOwnProfile()?.data?.id;
    const [modalState, setModalState] = useState<null | 'delete' | 'delete_assigned_availability' | 'more'>(null);
    const formattedTimeAgo = useUpdatingFormattedTimeAgo(
        data && 'start_at' in data ? new Date(data.start_at) : moment().startOf('day').toDate(),
    );
    const assignedAvailability = useGetAssignedParkingAvailability(
        data && 'assigned_availability_id' in data ? data.assigned_availability_id : null,
    );

    const assignedRequests = useMemo(() => (data && 'assigned_requests' in data ? data.assigned_requests : []), [data]);

    const availableSlots = useMemo(() => {
        if (data && 'assigned_requests' in data) {
            return getAvailabilityNonTakenSlots(data, assignedRequests);
        }
        return [];
    }, [data, assignedRequests]);

    if (status === 'pending') {
        return <BookingSharingSelectedSkeletonLoader />;
    }

    if (error) {
        throw error;
    }
    if (!data) {
        throw new Error('No data');
    }

    if ('status' in data && data.status === 'deleted') {
        return (
            <Container action={undefined}>
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <HeimeText variant="title" style={{ marginBottom: screenMargin }}>
                        {t(`bookingSharing:deleted_${params.type}_title`)}
                    </HeimeText>
                    <SecondaryButton
                        text={t('navigation:go_back')}
                        onPress={() => (canGoBack() ? goBack() : navigate('StartScreen', undefined))}
                    />
                </View>
            </Container>
        );
    }

    const handleOffer = () => {
        track('Pressed offer parking sharing request in selected');
        navigate('BookingSharingNew', { variant: 'availability', requestId: data.id });
    };

    const handleBook = () => {
        track('Pressed book parking sharing request in selected');
        navigate('BookingSharingNew', { variant: 'request', availabilityId: data.id });
    };

    const handleEditPress = () => {
        if (params.type === 'request') {
            track('Pressed edit parking sharing request in selected');
            navigate('BookingSharingNew', { variant: 'request', requestToEdit: data.id });
        } else {
            track('Pressed edit parking sharing availability in selected');
            navigate('BookingSharingNew', { variant: 'availability', availabilityToEdit: data.id });
        }
    };

    const handleMessage = () => {
        track('Pressed message parking sharing request in selected');
        onChat(data.creator_id);
    };

    const handleEditAssignedAvailability = () => {
        if ('status' in data && data.status === 'assigned') {
            track('Pressed edit parking sharing availability in selected');
            navigate('BookingSharingNew', {
                variant: 'availability',
                availabilityToEdit: data.assigned_availability_id,
            });
        }
    };
    const handleDeleteAssignedAvailability = () => {
        track('Pressed delete parking sharing availability in selected');
        setModalState('delete_assigned_availability');
    };
    const handleDeleteRequest = () => {
        track('Pressed delete parking sharing request in selected');
        setModalState('delete');
    };

    const getContextMenuAction = (action: () => void) => () => {
        setModalState(null);
        action();
    };

    const timeRange = formatBookingTimeRange(new Date(data.start_at), new Date(data.end_at), {
        t,
        includeOrdinal: true,
    });

    const isOwn = ownId === (data && 'creator_id' in data ? data.creator_id : undefined);
    const haveProvidedTimeRange =
        'status' in data && data.status === 'assigned' && data.assigned_availability_creator === ownId;
    const isInThePast = moment().isAfter(data.end_at);

    return (
        <Container
            action={
                !isInThePast && (isOwn || haveProvidedTimeRange) ? (
                    <ContextMenu
                        onToggle={() => setModalState((current) => (current === 'more' ? null : 'more'))}
                        isOpen={modalState === 'more'}
                        actions={
                            isOwn
                                ? [
                                      {
                                          text: t(`bookingSharing:edit_${params.type}`),
                                          onPress: getContextMenuAction(handleEditPress),
                                          type: 'normal' as const,
                                      },
                                      {
                                          text: moment(data.start_at).isBefore(moment().endOf('minute'))
                                              ? t(`bookingSharing:delete_ongoing_${params.type}`)
                                              : t(`bookingSharing:delete_${params.type}`),
                                          onPress: getContextMenuAction(handleDeleteRequest),
                                          type: 'danger' as const,
                                      },
                                  ]
                                : [
                                      {
                                          text: t('bookingSharing:edit_availability_assigned'),
                                          onPress: getContextMenuAction(handleEditAssignedAvailability),
                                          type: 'normal' as const,
                                      },
                                      {
                                          text: moment(
                                              assignedAvailability?.start_at ?? moment().add(1, 'hour'),
                                          ).isBefore(moment().endOf('minute'))
                                              ? t('bookingSharing:delete_ongoing_availability_assigned')
                                              : t('bookingSharing:delete_availability_assigned'),
                                          onPress: getContextMenuAction(handleDeleteAssignedAvailability),
                                          type: 'danger' as const,
                                      },
                                  ]
                        }
                        trigger={
                            <View
                                style={{
                                    justifyContent: 'center',
                                    paddingHorizontal: screenMargin,
                                    paddingVertical: smallestMargin,
                                }}
                            >
                                <Icon name={'moreFilled'} color="black" />
                            </View>
                        }
                    />
                ) : undefined
            }
        >
            <Animated.View style={{ flex: 1 }} entering={FadeIn.duration(250)}>
                <QueryItemView
                    isRefreshing={isRefetching}
                    onRefresh={refetch}
                    containerStyle={{ flex: 1, marginHorizontal: screenMargin, gap: smallestMargin }}
                >
                    <UserListItem
                        id={data.creator_id}
                        titleColor={theme.main}
                        noBottomBorder
                        desc={formattedTimeAgo}
                        avatarStyle={{
                            width: WW * 0.12,
                            height: WW * 0.12,
                            borderRadius: (WW * 0.15) / 2,
                        }}
                        containerStyle={{ paddingBottom: 0 }}
                    />
                    {isInThePast && params.type === 'availability' ? null : <BookingSharingTag type={params.type} />}
                    <HeimeText variant="title" style={{ textAlign: 'left' }}>
                        {t(`bookingSharing:${params.type}_parking_title`)}
                    </HeimeText>
                    <BookingSharingDetails {...data} />
                    {'assigned_requests' in data && data.assigned_requests.length > 0 ? (
                        <View>
                            <HeimeText variant="subtitle" style={{ color: theme.darkGray, fontWeight: 'bold' }}>
                                {t('bookingSharing:assigned_requests_title')}
                            </HeimeText>
                            {assignedRequests.map((request) => (
                                <BookingSharingItem
                                    key={request.id}
                                    {...request}
                                    actionable={request.creator_id === ownId ? 'noButton' : false}
                                />
                            ))}
                        </View>
                    ) : null}
                    {assignedAvailability ? (
                        <View>
                            <HeimeText variant="subtitle" style={{ color: theme.darkGray, fontWeight: 'bold' }}>
                                {t('bookingSharing:assigned_availability_title')}
                            </HeimeText>
                            {assignedAvailability.creator_id === ownId || data.creator_id === ownId ? (
                                <HeimeText variant="subtitle" style={{ color: theme.darkGray }}>
                                    {assignedAvailability.content}
                                </HeimeText>
                            ) : null}
                            <BookingSharingItem
                                hideContent={!isOwn && assignedAvailability.creator_id !== ownId}
                                timeRange={assignedAvailability.creator_id !== ownId ? timeRange : undefined}
                                actionable
                                {...assignedAvailability}
                            />
                        </View>
                    ) : null}
                    <View style={{ flex: 1 }} />
                    {isInThePast ? null : params.type === 'request' ? (
                        isOwn ? (
                            <PrimaryButton text={t('bookingSharing:edit')} onPress={handleEditPress} />
                        ) : 'status' in data && data.status === 'requested' ? (
                            <PrimaryButton text={t('bookingSharing:offer')} onPress={handleOffer} />
                        ) : null
                    ) : isOwn ? (
                        <PrimaryButton text={t('bookingSharing:edit')} onPress={handleEditPress} />
                    ) : availableSlots.length !== 0 ? (
                        <PrimaryButton text={t('bookingSharing:book')} onPress={handleBook} />
                    ) : null}
                    {isOwn ? null : (
                        <SecondaryButton
                            text={t('bookingSharing:message')}
                            onPress={handleMessage}
                            status={isLoadingChat ? 'loading' : null}
                        />
                    )}
                </QueryItemView>
                {modalState === 'delete' ? (
                    <DeleteBookingSharingModal
                        {...(data && 'assigned_requests' in data
                            ? {
                                  type: 'availability',
                                  item: data,
                              }
                            : {
                                  type: 'request',
                                  item: data,
                              })}
                        onDismiss={() => setModalState(null)}
                    />
                ) : modalState === 'delete_assigned_availability' && assignedAvailability ? (
                    <DeleteBookingSharingModal
                        type="availability"
                        item={assignedAvailability}
                        onDismiss={() => setModalState(null)}
                    />
                ) : null}
            </Animated.View>
        </Container>
    );
};

const Container = ({
    children,
    action,
}: PropsWithChildren<{
    action: ComponentProps<typeof HeaderWithNav>['action'];
}>) => {
    const { theme } = useThemeContext();
    const { t } = useTranslation();
    return (
        <SafeAreaView
            edges={['bottom', 'left', 'right']}
            style={{ backgroundColor: theme.mainBackground, height: '100%' }}
        >
            <HeaderWithNav title={t('bookingSharing:parking_title')} action={action} />
            {children}
        </SafeAreaView>
    );
};

export default BookingSharingRequestSelected;
